import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import CssBaseline from '@mui/material/CssBaseline';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import { grey } from '@mui/material/colors';
import useScrollTrigger from '@mui/material/useScrollTrigger';

//icons
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import CropFreeIcon from '@mui/icons-material/CropFree';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PeopleIcon from '@mui/icons-material/People';
import PlaceIcon from '@mui/icons-material/Place';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import EventNoteIcon from '@mui/icons-material/EventNote';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import EventIcon from '@mui/icons-material/Event';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import LogoutIcon from '@mui/icons-material/Logout';
import Divider from '@mui/material/Divider';
import ListItemText from '@mui/material/ListItemText';
import LoginIcon from '@mui/icons-material/Login';
import MenuIcon from '@mui/icons-material/Menu';
import Toolbar from '@mui/material/Toolbar';
import Snackbar from '@mui/material/Snackbar';
import {
  useLocation,
  useNavigate,
  Routes,
  Route,
  Navigate,
  useSearchParams,
} from 'react-router-dom';
import classNames from 'classnames';
import { Logo } from './Logo';
import { APP_NAME } from '../constants';
import CircularProgress from '@mui/material/CircularProgress';
import { useProfile } from 'api/hooks';
import SignIn from 'pages/SignIn';
import { Users } from 'pages/admin/Users';
import { Permissions } from 'pages/admin/Permissions';
import { Organizations } from 'pages/admin/Organizations';
import { OrganizationEdit } from 'pages/OrganizationEdit';
import { EventTypes } from 'pages/admin/EventTypes';
import { TicketTypes } from 'pages/admin/TicketTypes';
import { TicketTypeSets } from 'pages/admin/TicketTypeSets';
import { Venues } from 'pages/admin/Venues';
import { UserVerify } from 'pages/UserVerify';
import { UserEdit } from 'pages/UserEdit';
import ErrorBoundary from 'ErrorBoundary';
import {
  Avatar,
  Box,
  Grow,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { uniqBy } from 'lodash';
import Autocomplete from '@mui/material/Autocomplete';
import { useEffect, useMemo } from 'react';
import { useMedia } from 'react-use';

import { VenueEdit } from 'pages/admin/VenueEdit';
import { Events } from 'pages/admin/Events';
import { Orders } from 'pages/admin/Orders';
import { AdminEvent } from 'pages/admin/Event';
import { AdminOrganization } from 'pages/admin/Organization';
import { Summary } from 'pages/admin/Summary';
import CheckoutResult from 'pages/CheckoutResult';

const FrontPage = React.lazy(() => import('../pages/index/FrontPage'));
const OrderPage = React.lazy(() => import('../pages/OrderPage'));
const GateScanner = React.lazy(() => import('../pages/admin/GateScanner'));
const TicketScanner = React.lazy(() => import('../pages/admin/TicketScanner'));

const drawerWidth = 240;

const settings = ['Profile', 'Account', 'Dashboard', 'Logout'];

function PaperComponent(paperProps, ref) {
  return (
    <Grow in>
      <Paper {...paperProps} ref={ref} />
    </Grow>
  );
}

export function ResponsiveDrawer() {
  const { profile, logout, isLoading } = useProfile();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('r');
  const isSignedIn = !!profile;
  const isDarkTheme = useMedia('(prefers-color-scheme: dark)');
  const isMobile = useMedia('(max-width: 900px)');
  const theme = useTheme();

  const PaperComponentForward = React.forwardRef(PaperComponent);
  //console.log(profile);

  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(
    null,
  );
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null,
  );

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const scrollTrigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
    target: window,
  });

  const userOrganizations: any = useMemo(() => {
    return profile?.roles?.length > 0
      ? uniqBy(
          profile?.roles.map((role: any) => {
            return {
              name: role.organization?.name || '',
              id: role.organizationId || '',
              type: role.organization?.type || '',
              dataSource: role.organization?.dataSource || '',
              enabled: role.organization?.enabled || false,
            };
          }),
          'id',
        )
      : [];
  }, [profile]);

  const isSuperAdmin =
    profile?.roles?.some((role) => role.type === 'SUPER_ADMIN') ?? false;

  const userOrganizationOptions = useMemo(() => {
    if (isSuperAdmin) {
      return [...userOrganizations].slice(1);
    } else {
      return [...userOrganizations];
    }
  }, [isSuperAdmin, userOrganizations]);

  const [selectedOrganization, setSelectedOrganization] = React.useState<any>(
    userOrganizationOptions.length > 0 ? userOrganizationOptions[0] : undefined,
  );

  useEffect(() => {
    if (!selectedOrganization) {
      setSelectedOrganization(userOrganizationOptions[0]);
    }
  }, [selectedOrganization, userOrganizationOptions]);

  const [mobileOpen, setMobileOpen] = React.useState(false);

  const [snackbarOpen, setSnackbarOpen] = React.useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const isAdmin =
    (profile?.roles?.some((role) => role.type === 'ORGANIZATION_ADMIN') ||
      isSuperAdmin) ??
    false;

  const isEventConfigurator =
    (profile?.roles?.some((role) => role.type === 'EVENT_CONFIGURATOR') ||
      isAdmin ||
      isSuperAdmin) ??
    false;

  const isEventCreator =
    (profile?.roles?.some((role) => role.type === 'EVENT_CREATOR') ||
      isAdmin ||
      isEventConfigurator ||
      isSuperAdmin) ??
    false;

  const isEventReporter =
    (profile?.roles?.some((role) => role.type === 'EVENT_REPORTER') ||
      isAdmin ||
      isEventConfigurator ||
      isEventCreator ||
      isSuperAdmin) ??
    false;

  const isTicketInvalidator =
    (profile?.roles?.some((role) => role.type === 'TICKET_INVALIDATOR') ||
      isAdmin ||
      isSuperAdmin) ??
    false;

  const isTicketChecker =
    (profile?.roles?.some((role) => role.type === 'TICKET_CHECKER') ||
      isAdmin ||
      isSuperAdmin) ??
    false;

  const drawer = (
    <>
      {/* {isMobile && ( */}
      <Toolbar>
        <Box className="logo-and-sitename" sx={{ width: '100%' }}>
          <Typography variant="h6" noWrap component="div">
            <Logo />
            {APP_NAME}
          </Typography>
        </Box>
      </Toolbar>
      {/* )} */}
      <List>
        <div
          className={classNames({
            'list-item-active': location.pathname === '/',
          })}
        >
          <ListItem
            button
            onClick={() => {
              navigate('/');
              handleDrawerToggle();
            }}
          >
            <ListItemIcon>
              <EventIcon />
            </ListItemIcon>
            <ListItemText primary={'Events'} />
          </ListItem>
        </div>
        {/*
        <Divider sx={{ margin: '8px 0' }} />
        <Typography
          component="h3"
          sx={{
            // eslint-disable-next-line sonarjs/no-duplicate-string
            margin: '1rem 16px 0.5rem',
            textTransform: 'uppercase',
            fontSize: '0.875rem',
            fontWeight: '500',
            // eslint-disable-next-line sonarjs/no-duplicate-string
            letterSpacing: '0.03125rem',
          }}
        >
          Events
        </Typography>
        */}

        {(isTicketChecker || isTicketInvalidator) && (
          <>
            <Divider sx={{ margin: '8px 0' }} />
            <Typography
              component="h3"
              sx={{
                // eslint-disable-next-line sonarjs/no-duplicate-string
                margin: '1rem 16px 0.5rem',
                textTransform: 'uppercase',
                fontSize: '0.875rem',
                fontWeight: '500',
                // eslint-disable-next-line sonarjs/no-duplicate-string
                letterSpacing: '0.03125rem',
              }}
            >
              Tools
            </Typography>
          </>
        )}
        {isTicketChecker && (
          <div
            className={classNames({
              // eslint-disable-next-line sonarjs/no-duplicate-string
              'list-item-active': location.pathname === '/ticket-info',
            })}
          >
            <ListItem
              button
              onClick={() => {
                navigate('/ticket-info');
                handleDrawerToggle();
              }}
            >
              <ListItemIcon>
                <CropFreeIcon />
              </ListItemIcon>
              <ListItemText primary={'Ticket info'} />
            </ListItem>
          </div>
        )}
        {isTicketInvalidator && (
          <div
            className={classNames({
              'list-item-active': location.pathname === '/gate-scanner',
            })}
          >
            <ListItem
              button
              onClick={() => {
                navigate('/gate-scanner');
                handleDrawerToggle();
              }}
            >
              <ListItemIcon>
                <QrCodeScannerIcon />
              </ListItemIcon>
              <ListItemText primary={'Gate scanner'} />
            </ListItem>
          </div>
        )}

        {(isAdmin || null) && (
          <div
            className={classNames({
              'list-item-active': location.pathname === '/admin/summary',
            })}
          >
            <ListItem
              button
              onClick={() => {
                navigate('/admin/summary');
                handleDrawerToggle();
              }}
            >
              <ListItemIcon>
                <SummarizeOutlinedIcon />
              </ListItemIcon>
              <ListItemText primary={'Summary'} />
            </ListItem>
          </div>
        )}

        {(isAdmin ||
          isEventCreator ||
          isEventConfigurator ||
          isEventReporter) && (
          <>
            <Divider sx={{ margin: '8px 0' }} />
            <Typography
              component="h3"
              sx={{
                margin: '1rem 16px 0.5rem',
                textTransform: 'uppercase',
                fontSize: '0.875rem',
                fontWeight: '500',
                letterSpacing: '0.03125rem',
              }}
            >
              Administer
            </Typography>
          </>
        )}
        {(isEventCreator || null) && (
          <div
            className={classNames({
              'list-item-active': location.pathname === '/admin/events',
            })}
          >
            <ListItem
              button
              onClick={() => {
                navigate('/admin/events');
                handleDrawerToggle();
              }}
            >
              <ListItemIcon>
                <EventIcon />
              </ListItemIcon>
              <ListItemText primary={'Events'} />
            </ListItem>
          </div>
        )}
        {isEventCreator && (
          <>
            <div
              className={classNames({
                'list-item-active': location.pathname === '/admin/event-types',
              })}
            >
              <ListItem
                button
                onClick={() => {
                  navigate('/admin/event-types');
                  handleDrawerToggle();
                }}
              >
                <ListItemIcon>
                  <EventNoteIcon />
                </ListItemIcon>
                <ListItemText primary={'Event types'} />
              </ListItem>
            </div>
          </>
        )}

        {(isEventConfigurator || null) && (
          <div
            className={classNames({
              'list-item-active': location.pathname === '/admin/ticket-types',
            })}
          >
            <ListItem
              button
              onClick={() => {
                navigate('/admin/ticket-types');
                handleDrawerToggle();
              }}
            >
              <ListItemIcon>
                <LocalActivityIcon />
              </ListItemIcon>
              <ListItemText primary={'Ticket types'} />
            </ListItem>
          </div>
        )}
        {(isEventConfigurator || null) && (
          <div
            className={classNames({
              'list-item-active':
                location.pathname === '/admin/ticket-type-sets',
            })}
          >
            <ListItem
              button
              onClick={() => {
                navigate('/admin/ticket-type-sets');
                handleDrawerToggle();
              }}
            >
              <ListItemIcon>
                <ConfirmationNumberIcon />
              </ListItemIcon>
              <ListItemText primary={'Ticket type sets'} />
            </ListItem>
          </div>
        )}
        {(isEventConfigurator || null) && (
          <div
            className={classNames({
              'list-item-active': location.pathname === '/admin/venues',
            })}
          >
            <ListItem
              button
              onClick={() => {
                navigate('/admin/venues');
                handleDrawerToggle();
              }}
            >
              <ListItemIcon>
                <PlaceIcon />
              </ListItemIcon>
              <ListItemText primary={'Venues'} />
            </ListItem>
          </div>
        )}
        {isSuperAdmin && (
          <>
            <div
              className={classNames({
                'list-item-active': location.pathname === '/admin/orders',
              })}
            >
              <ListItem
                button
                onClick={() => {
                  navigate('/admin/orders');
                  handleDrawerToggle();
                }}
              >
                <ListItemIcon>
                  <UploadFileIcon />
                </ListItemIcon>
                <ListItemText primary={'Orders'} />
              </ListItem>
            </div>
          </>
        )}
        {(isAdmin || null) && (
          <div
            className={classNames({
              'list-item-active': location.pathname === '/admin/permissions',
            })}
          >
            <ListItem
              button
              onClick={() => {
                navigate('/admin/permissions');
                handleDrawerToggle();
              }}
            >
              <ListItemIcon>
                <SupervisorAccountIcon />
              </ListItemIcon>
              <ListItemText primary={'Permissions'} />
            </ListItem>
          </div>
        )}
        {isAdmin && (
          <>
            <div
              className={classNames({
                'list-item-active':
                  location.pathname === '/admin/organizations',
              })}
            >
              <ListItem
                button
                onClick={() => {
                  navigate('/admin/organizations');
                  handleDrawerToggle();
                }}
              >
                <ListItemIcon>
                  <CorporateFareIcon />
                </ListItemIcon>
                <ListItemText primary={'Organizations'} />
              </ListItem>
            </div>
          </>
        )}
        {isSuperAdmin && (
          <div
            className={classNames({
              'list-item-active': location.pathname === '/admin/users',
            })}
          >
            <ListItem
              button
              onClick={() => {
                navigate('/admin/users');
                handleDrawerToggle();
              }}
            >
              <ListItemIcon>
                <PeopleIcon />
              </ListItemIcon>
              <ListItemText primary={'Users'} />
            </ListItem>
          </div>
        )}
        {!isSignedIn && (
          <>
            <Divider sx={{ margin: '8px 0' }} />
            <div
              className={classNames({
                'list-item-active': location.pathname === '/signin',
              })}
            >
              <ListItem
                button
                onClick={() => {
                  navigate('/signin');
                  handleDrawerToggle();
                }}
              >
                <ListItemIcon>
                  <LoginIcon />
                </ListItemIcon>
                <ListItemText primary={'Sign in'} />
              </ListItem>
            </div>
            <div
              className={classNames({
                'list-item-active': location.pathname === '/signup',
              })}
            >
              <ListItem
                button
                onClick={() => {
                  navigate('/signup');
                  handleDrawerToggle();
                }}
              >
                <ListItemIcon>
                  <AccountBoxIcon />
                </ListItemIcon>
                <ListItemText primary={'Sign up'} />
              </ListItem>
            </div>
          </>
        )}
        {isSignedIn && isMobile && (
          <>
            <Divider sx={{ margin: '8px 0' }} />
            <Box sx={{ maxWidth: '100%' }}>
              <Typography
                component="h3"
                sx={{
                  margin: '1rem 16px 0.5rem',
                  textTransform: 'uppercase',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  wordBreak: 'break-all',
                  letterSpacing: '0.03125rem',
                  lineHeight: '1.1rem',
                }}
              >
                {profile.username}
              </Typography>
            </Box>
            <div
              className={classNames({
                'list-item-active': location.pathname === '/user-edit',
              })}
            >
              <ListItem
                button
                onClick={() => {
                  navigate(`/user-edit/${profile.id}`);
                  handleDrawerToggle();
                }}
              >
                <ListItemIcon>
                  <AccountBoxIcon />
                </ListItemIcon>
                <ListItemText primary={'Edit account'} />
              </ListItem>
            </div>

            <div>
              <ListItem
                button
                onClick={() => {
                  logout();
                  navigate('/');
                  setSnackbarOpen(true);
                  handleDrawerToggle();
                }}
              >
                <ListItemIcon>
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText primary={'Logout'} />
              </ListItem>
            </div>
          </>
        )}
      </List>
    </>
  );

  const container =
    window !== undefined ? () => window.document.body : undefined;

  const handleClose = (
    event: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }

    setSnackbarOpen(false);
  };

  return (
    <Box
      className={isDarkTheme ? 'dark-theme' : 'light-theme'}
      sx={{ display: 'flex' }}
    >
      <CssBaseline />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleClose}
        message="You have been logged out"
      />
      {/*
      ml: { sm: `${drawerWidth}px` }, 
      zIndex: (theme) => isMobile ? theme.zIndex.drawer - 1 : theme.zIndex.drawer + 1, */}
      <AppBar
        className="appbar"
        position="fixed"
        sx={{
          width: { drawerWidth },
          height: '56px',
          color: isDarkTheme
            ? 'rgba(255 255 255/0.8)!important'
            : 'rgba(0 0 0/0.75)!important',
          background: !isDarkTheme
            ? 'rgba(244 246 249/0.8)'
            : 'rgba(24 24 24/0.83)',

          backdropFilter: 'blur(10px)',
        }}
      >
        <Toolbar
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0, md: '10px' },
            minHeight: { md: '56px' },
          }}
        >
          {/* display: {
            md: 'none',
          }, */}
          <IconButton
            className="menu-btn"
            color="inherit"
            aria-label="drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ ml: { xs: '-10px', md: 0 }, mr: 2 }} //{ sm: 'none' }
          >
            <MenuIcon />
          </IconButton>
          <Box
            onClick={() => navigate('/')}
            sx={{
              cursor: 'pointer',

              width: 'auto',
            }}
          >
            <Logo />
            <Typography variant="h6" noWrap component="div">
              {APP_NAME}
            </Typography>
          </Box>
          {userOrganizations?.length > 0 && (
            <Box
              className="field-with-icon"
              sx={{
                position: 'relative',
                width: { sm: '300px' },
                flexGrow: 1,
                maxWidth: '300px',
              }}
            >
              <Autocomplete
                PaperComponent={PaperComponentForward}
                style={{ marginLeft: '12px', marginTop: '1px' }}
                disablePortal
                color="#ffffff"
                id="combo-box"
                size="small"
                options={userOrganizationOptions}
                getOptionLabel={(option: any) => option.name}
                defaultValue={userOrganizationOptions[0]}
                isOptionEqualToValue={(option: any, value: any) => {
                  return option.id === value.id;
                }}
                onChange={(e: any, newValue: any) => {
                  setSelectedOrganization(newValue);
                }}
                sx={{ width: { sm: '300px' }, flexGrow: 1, maxWidth: '300px' }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    style={{
                      color: '#ffffff',
                    }}
                  />
                )}
              />

              <BusinessOutlinedIcon
                className="field-icon"
                sx={{
                  position: 'absolute',
                  top: '8px',
                  left: '22px',
                  opacity: 0.8,
                }}
              />
            </Box>
          )}
          {isSignedIn && !isMobile && (
            <Box sx={{ flexGrow: 0, marginLeft: 'auto', px: '14px' }}>
              <IconButton onClick={handleOpenUserMenu} size="small">
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: grey[600],
                  }}
                ></Avatar>
              </IconButton>

              <Menu
                sx={{ mt: '34px' }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                <MenuItem sx={{ opacity: '1!important' }} disabled>
                  <Typography textAlign="center">{profile.username}</Typography>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    navigate(`/user-edit/${profile.id}`);
                    handleCloseUserMenu();
                  }}
                >
                  <ListItemIcon>
                    <AccountBoxIcon />
                  </ListItemIcon>
                  <Typography textAlign="center">Edit account</Typography>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    logout();
                    navigate('/');
                    setSnackbarOpen(true);
                    handleCloseUserMenu();
                  }}
                >
                  <ListItemIcon>
                    <LogoutIcon />
                  </ListItemIcon>
                  <Typography textAlign="center">Logout</Typography>
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Toolbar>
      </AppBar>
      {/* sx={{ width: { md: drawerWidth }, flexShrink: { sm: 0 } }} */}
      <Box component="nav" aria-label="mailbox folders">
        {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
        {/* variant= {isMobile ? 'temporary' : 'permanent'} 
         mt: { xs: '0', md: '64px' }, */}
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: 'block', //display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              flex: '0 0 auto',
              flexGrow: 1,
              flexShrink: 0,
            },
          }}
        >
          {drawer}
        </Drawer>
        {/* 
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        */}
      </Box>
      <Box
        component="main"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          p: '0 16px 16px 16px',
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        {isLoading && (
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <CircularProgress />
          </Box>
        )}
        <ErrorBoundary fallback={'...'}>
          <React.Suspense
            fallback={
              <Box sx={{ textAlign: 'center', mt: 6 }}>
                <CircularProgress />
              </Box>
            }
          >
            {!isLoading && (
              <Routes>
                <Route path="/" element={<FrontPage />} />
                <Route
                  path="/signin"
                  element={
                    isSignedIn ? <Navigate to={redirect || '/'} /> : <SignIn />
                  }
                />

                {/* user routes */}
                <Route path="/signup" element={<UserEdit />} />

                <Route
                  path="/user-edit/:userId"
                  element={isSignedIn ? <UserEdit /> : <Navigate to="/" />}
                />

                <Route path="/verify/:token" element={<UserVerify />} />

                <Route
                  path="/admin/users"
                  element={
                    isAdmin ? (
                      <Users selectedOrganization={selectedOrganization} />
                    ) : (
                      <Navigate to="/" />
                    )
                  }
                />

                <Route
                  path="/admin/permissions"
                  element={
                    isAdmin ? (
                      <Permissions
                        selectedOrganization={selectedOrganization}
                      />
                    ) : (
                      <Navigate to="/" />
                    )
                  }
                />

                {/* organizations routes */}

                <Route
                  path="/admin/organizations"
                  element={
                    isAdmin ? (
                      <Organizations
                        selectedOrganization={selectedOrganization}
                      />
                    ) : (
                      <Navigate to="/" />
                    )
                  }
                />

                <Route
                  path="/admin/organization/:organizationId"
                  element={
                    isAdmin ? (
                      <AdminOrganization
                        selectedOrganization={selectedOrganization}
                      />
                    ) : (
                      <Navigate to="/" />
                    )
                  }
                />

                <Route path="/admin/organization-edit">
                  <Route
                    path="/admin/organization-edit"
                    //element={isAdmin ? <OrganizationEdit /> : <Navigate to="/" />}
                    element={
                      isSignedIn ? <OrganizationEdit /> : <Navigate to="/" />
                    }
                  />
                  <Route
                    path="/admin/organization-edit/:organizationId"
                    element={
                      isAdmin ? <OrganizationEdit /> : <Navigate to="/" />
                    }
                  />
                </Route>
                <Route
                  path="/admin/events"
                  element={
                    isAdmin ||
                    isEventConfigurator ||
                    isEventCreator ||
                    isEventReporter ? (
                      <Events selectedOrganization={selectedOrganization} />
                    ) : (
                      <Navigate to="/" />
                    )
                  }
                />
                <Route
                  path="/admin/event/:eventId"
                  element={
                    isAdmin ||
                    isEventConfigurator ||
                    isEventCreator ||
                    isEventReporter ? (
                      <AdminEvent selectedOrganization={selectedOrganization} />
                    ) : (
                      <Navigate to="/" />
                    )
                  }
                />
                <Route
                  path="/admin/event-types"
                  element={
                    isAdmin || isEventConfigurator ? (
                      <EventTypes selectedOrganization={selectedOrganization} />
                    ) : (
                      <Navigate to="/" />
                    )
                  }
                />
                <Route
                  path="/admin/ticket-types"
                  element={
                    isAdmin || isEventConfigurator ? (
                      <TicketTypes
                        selectedOrganization={selectedOrganization}
                      />
                    ) : (
                      <Navigate to="/" />
                    )
                  }
                />
                <Route
                  path="/admin/ticket-type-sets"
                  element={
                    isAdmin || isEventConfigurator ? (
                      <TicketTypeSets
                        selectedOrganization={selectedOrganization}
                      />
                    ) : (
                      <Navigate to="/" />
                    )
                  }
                />
                <Route
                  path="/admin/venues"
                  element={
                    isAdmin || isEventConfigurator ? (
                      <Venues selectedOrganization={selectedOrganization} />
                    ) : (
                      <Navigate to="/" />
                    )
                  }
                />
                <Route path="/admin/venue-edit">
                  <Route
                    path="/admin/venue-edit/"
                    element={
                      isAdmin || isEventConfigurator ? (
                        <VenueEdit
                          selectedOrganization={selectedOrganization}
                        />
                      ) : (
                        <Navigate to="/" />
                      )
                    }
                  />
                  <Route
                    path="/admin/venue-edit/:venueId"
                    element={
                      isAdmin || isEventConfigurator ? (
                        <VenueEdit
                          selectedOrganization={selectedOrganization}
                        />
                      ) : (
                        <Navigate to="/" />
                      )
                    }
                  />
                </Route>

                <Route path="/event/:eventId" element={<OrderPage />} />

                <Route
                  path="/ticket-info"
                  element={
                    isSignedIn ? (
                      isTicketChecker ? (
                        <TicketScanner />
                      ) : (
                        <Box sx={{ textAlign: 'center', mt: 6 }}>
                          You do not have permission to view this page.
                        </Box>
                      )
                    ) : (
                      <Navigate to={'/login?r=/ticket-info'} />
                    )
                  }
                />

                <Route
                  path="/gate-scanner"
                  element={
                    isSignedIn ? (
                      isTicketInvalidator ? (
                        <GateScanner />
                      ) : (
                        <Box sx={{ textAlign: 'center', mt: 6 }}>
                          You do not have permission to view this page.
                        </Box>
                      )
                    ) : (
                      <Navigate to={'/login?r=/gate-scanner'} />
                    )
                  }
                />
                <Route
                  path="/admin/orders"
                  element={
                    isAdmin ? (
                      <Orders selectedOrganization={selectedOrganization} />
                    ) : (
                      <Navigate to="/" />
                    )
                  }
                />
                <Route
                  path="/admin/summary"
                  element={
                    isAdmin ? (
                      <Summary selectedOrganization={selectedOrganization} />
                    ) : (
                      <Navigate to="/" />
                    )
                  }
                />
                <Route path="/checkout/result" element={<CheckoutResult />} />
              </Routes>
            )}
          </React.Suspense>
        </ErrorBoundary>
      </Box>
    </Box>
  );
}
